import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';

/**
 * `/masters/employee-grades` (v0.4後半 月次実績一括入力 第2フェーズ I-1b、§3-2、D-1017)。
 * `employee_grades` (`apps/api/src/routes/employee-org-masters.ts`) の管理画面。
 * AttendanceReasonCodesPage.tsx と同型のテナント全体共有マスタ (own_store の manage 保有者には
 * 書換えを許さない、requireTenantWideManage と同じ規約、§8-50)。
 */

type Grade = { grade_id: string; code: string; label: string; sort_order: number };

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

type Draft = { grade_id: string | null; code: string; label: string; sort_order: string };

export function EmployeeGradesPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'skill', 'manage');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Grade | null>(null);

  const { data, isLoading } = useQuery<Grade[]>({
    queryKey: ['employee-grades'],
    queryFn: () => apiFetch<Grade[]>('/masters/employee-grades'),
  });
  const grades = [...(data ?? [])].sort((a, b) => a.sort_order - b.sort_order);

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const payload = { code: d.code.trim(), label: d.label.trim(), sort_order: Number(d.sort_order) || 0 };
      return d.grade_id
        ? apiFetch(`/masters/employee-grades/${d.grade_id}`, { method: 'PUT', body: JSON.stringify({ label: payload.label, sort_order: payload.sort_order }) })
        : apiFetch('/masters/employee-grades', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee-grades'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/employee-grades/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee-grades'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1">グレードマスタ</h1>
        <p className="text-sm text-text-sub mt-1">社員に設定するグレード (等級) を管理します。並び順は大きいほど上位として扱われます。</p>
        {!canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です（登録・編集には全店舗の管理権限が必要です）。</p>}
      </div>

      {canManage && (
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setDraft({ grade_id: null, code: '', label: '', sort_order: String(grades.length) })}
          >グレードを追加</Button>
        </div>
      )}

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : grades.length === 0 ? (
        <EmptyState message="グレードがまだありません" description={canManage ? '「グレードを追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {grades.map((g) => (
            <li key={g.grade_id} className="flex items-center justify-between gap-2 border border-surface-border rounded-lg bg-white p-3 flex-wrap">
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-text-body break-words">{g.label}</span>
                <span className="text-[11px] text-text-sub whitespace-nowrap">({g.code})</span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">並び順: {g.sort_order}</span>
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" aria-label="編集"
                    onClick={() => setDraft({ grade_id: g.grade_id, code: g.code, label: g.label, sort_order: String(g.sort_order) })}
                    className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                  <button type="button" aria-label="削除" onClick={() => setDeleteTarget(g)}
                    className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.grade_id ? 'グレードを編集' : 'グレードを追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50}
                className={inputCls} placeholder="例: G3" disabled={!!draft.grade_id} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={100}
                className={inputCls} placeholder="例: グレード3" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">並び順 (大きいほど上位)</label>
              <input type="number" min={0} value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} className={numberInputCls} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="グレードを削除" message={`「${deleteTarget?.label}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.grade_id)} />
    </div>
  );
}
